import { memo, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { EventCard } from './EventCard';
import PrefetchEventOnVisible from './PrefetchEventOnVisible';
import { buildEventForCardFromFeedEvent } from '../utils/eventFeedModel';
import { useEventHeadliners } from '../hooks/queries/useEventHeadliners';

const FeedEventCardRow = memo(function FeedEventCardRow({
  event,
  myStatus,
  joinSource,
  attachInfiniteRef,
  lastEventElementRef,
  onEventNavigate,
  onRSVP,
  headliners,
}) {
  const eventForCard = useMemo(() => buildEventForCardFromFeedEvent(event), [event]);

  const handleRSVP = async (eventId, status) => {
    await onRSVP(eventId, status, myStatus);
  };

  return (
    <PrefetchEventOnVisible eventId={event.event_id}>
      <div
        ref={attachInfiniteRef ? lastEventElementRef : null}
        onClick={(e) => {
          if (!e.target.closest('button')) {
            onEventNavigate(event.event_id);
          }
        }}
        className="cursor-pointer"
      >
        <EventCard
          event={eventForCard}
          variant="tile"
          headliners={headliners}
          userStatus={myStatus}
          onRSVP={handleRSVP}
          joinSource={joinSource}
        />
      </div>
    </PrefetchEventOnVisible>
  );
});

/**
 * Shared Events feed / search results grid — same EventCard layout as the main feed.
 */
export default function EventFeedResultsGrid({
  events,
  userRsvpByEvent,
  hasMore,
  loadingMore,
  joinSource,
  onEventNavigate,
  onRSVP,
  lastEventElementRef,
}) {
  const { data: headliners = {} } = useEventHeadliners(events.map((e) => e.event_id));
  return (
    <>
      {/* Two across on a phone (Sam's tile A, 2026-10-27); the headliners' Spotify ids come in one query per page. */}
      <div className="event-tiles">
        {events.map((event, index) => {
          const myStatus = userRsvpByEvent[event.event_id] ?? null;
          const isLastRow = index === events.length - 1;
          const attachInfiniteRef = hasMore && isLastRow;

          return (
            <FeedEventCardRow
              key={event.event_id}
              event={event}
              myStatus={myStatus}
              joinSource={joinSource}
              attachInfiniteRef={attachInfiniteRef}
              lastEventElementRef={lastEventElementRef}
              onEventNavigate={onEventNavigate}
              onRSVP={onRSVP}
              headliners={headliners[event.event_id] ?? null}
            />
          );
        })}
      </div>
      {hasMore && events.length > 0 && (
        <div className="flex h-20 w-full shrink-0 items-center justify-center pb-4">
          <Loader2
            className={`h-6 w-6 flex-none text-primary animate-spin transition-opacity duration-150 ${
              loadingMore ? 'opacity-100' : 'opacity-0'
            }`}
            aria-hidden={!loadingMore}
          />
        </div>
      )}
    </>
  );
}
